import { useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { IconX } from './Icons';
import LoginForm from './auth/LoginForm';
import styles from './LoginModal.module.css';

/**
 * 회원 전용 동작을 눌렀을 때 뜨는 로그인 모달.
 *
 * 로그인 폼은 필드 두 개뿐이라 모달에 담는다. 페이지를 떠나지 않으므로
 * 로그인 직후 하려던 동작(저장 등)을 그 자리에서 이어서 끝낼 수 있다.
 * 반면 회원가입은 인증번호 타이머가 도는 6단계라 모달에서 잘못 닫히면
 * 전부 날아가므로 페이지로 넘긴다.
 *
 * @param {() => void} onSuccess  로그인 성공 시 — 막혀 있던 동작을 여기서 이어간다
 */
export default function LoginModal({ isOpen, onClose, onSuccess }) {
  const location = useLocation();
  const boxRef = useRef(null);
  const lastFocused = useRef(null);

  // 로그인을 마치면 누르던 자리로 돌아오게 현재 경로를 들려 보낸다
  const from = `${location.pathname}${location.search}`;

  useEffect(() => {
    if (!isOpen) return undefined;

    lastFocused.current = document.activeElement;
    boxRef.current?.focus();

    // 모달 뒤 페이지가 같이 스크롤되지 않도록 잠근다
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;

      // 포커스가 모달 밖으로 새어나가지 않게 가둔다
      const items = boxRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (!items?.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
      lastFocused.current?.focus?.();
    };
  }, [isOpen, onClose]);

  return (
    <div
      className={`${styles.backdrop} ${isOpen ? styles.open : ''}`}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={styles.box}
        ref={boxRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="login-modal-title"
      >
        <button className={styles.close} onClick={onClose} aria-label="닫기">
          <IconX size={14} />
        </button>

        <h2 className={styles.title} id="login-modal-title">
          저장하려면 로그인이 필요해요
        </h2>

        <LoginForm onSuccess={() => { onClose(); onSuccess?.(); }} />

        <p className={styles.signupHint}>
          아직 계정이 없으신가요?{' '}
          <Link to="/signup" state={{ from }} onClick={onClose} className={styles.signupLink}>
            회원가입
          </Link>
        </p>
      </div>
    </div>
  );
}
